import React, { useState, useRef, useEffect } from "react";

// Color scheme type
export type CindraButtonColor = "blue" | "yellow" | "green" | "red" | "gray";

// Style type
export type CindraButtonStyle = "micro" | "normal";

// Props interface
export interface CindraButtonProps {
    // Style configuration
    style: CindraButtonStyle;

    // Color scheme (default: blue)
    color?: CindraButtonColor;

    // Icon element for the button
    icon: React.ReactNode;

    // Label text (used in "normal" style, ignored in "micro" style)
    label?: string;

    // Status text shown during loading (micro: replaces button, normal: replaces label)
    statusText?: string;

    // Minimum width for the button (optional, applies to "normal" style)
    minWidth?: number;

    // Disabled state
    disabled?: boolean;

    // Callbacks
    onClick?: (e: React.MouseEvent) => void | Promise<void>;
    onComplete?: (e: React.MouseEvent) => void | Promise<void>;

    // Tooltip text
    title?: string;

    // Loading state (controlled externally)
    isLoading?: boolean;

    // Whether to show loading state when isLoading is true (default: true)
    // Set to false to keep the button in its default state even when isLoading is true
    showLoadingState?: boolean;

    // Whether to use fade in/out animation when button appears/disappears (default: false)
    useFadeAnimation?: boolean;

    // Controls visibility for fade animation (only used when useFadeAnimation is true)
    isVisible?: boolean;

    // Duration for fade out animation in ms (default: 300)
    fadeOutDuration?: number;

    // Duration for fade in animation in ms (default: 600)
    fadeInDuration?: number;
}

// Color configuration
const colorConfig: Record<CindraButtonColor, {
    enabled: string;
    hover: string;
    disabled: string;
    border: string;
    text: string;
    spinner: string;
}> = {
    blue: {
        enabled: "bg-blue-600",
        hover: "hover:bg-blue-700",
        disabled: "bg-gray-400",
        border: "border-blue-600",
        text: "text-blue-600",
        spinner: "border-blue-600"
    },
    yellow: {
        enabled: "bg-yellow-500",
        hover: "hover:bg-yellow-600",
        disabled: "bg-gray-400",
        border: "border-yellow-500",
        text: "text-yellow-600",
        spinner: "border-yellow-600"
    },
    green: {
        enabled: "bg-green-600",
        hover: "hover:bg-green-700",
        disabled: "bg-gray-400",
        border: "border-green-600",
        text: "text-green-600",
        spinner: "border-green-600"
    },
    red: {
        enabled: "bg-red-600",
        hover: "hover:bg-red-700",
        disabled: "bg-gray-400",
        border: "border-red-600",
        text: "text-red-600",
        spinner: "border-red-600"
    },
    gray: {
        enabled: "bg-gray-600",
        hover: "hover:bg-gray-700",
        disabled: "bg-gray-400",
        border: "border-gray-600",
        text: "text-gray-600",
        spinner: "border-gray-600"
    }
};

export const CindraButton = React.forwardRef<HTMLDivElement, CindraButtonProps>(({
    color = "blue",
    disabled = false,
    fadeInDuration = 600,
    fadeOutDuration = 300,
    icon,
    isLoading = false,
    isVisible = true,
    label = "",
    minWidth,
    showLoadingState = true,
    statusText = "Loading...",
    style,
    title,
    useFadeAnimation = false,
    // Callbacks
    onClick,
    onComplete,
}, ref) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerDimensions, setContainerDimensions] = useState<{ width: number; height: number } | null>(null);

    const colors = colorConfig[color];

    // Calculate and lock container dimensions based on the larger of button or loading state
    useEffect(() => {
        if (!containerRef.current) return;

        // Measure both states to get the max bounding box
        const measureDimensions = () => {
            const container = containerRef.current;
            if (!container) return;

            // Get the current rendered dimensions
            const { width, height } = container.getBoundingClientRect();

            // For micro style, ensure we account for both button and loading states
            if (style === "micro") {
                // The loading state typically needs more width (spinner + text)
                // Button is just the icon
                const buttonWidth = buttonRef.current?.getBoundingClientRect().width || 0;
                const maxWidth = Math.max(width, buttonWidth, 95); // 95px is the original micro width
                const maxHeight = Math.max(height, 24); // Reasonable default height

                setContainerDimensions({ width: maxWidth, height: maxHeight });
            } else {
                // For normal style, respect minWidth
                const maxWidth = minWidth ? Math.max(width, minWidth) : width;
                setContainerDimensions({ width: maxWidth, height });
            }
        };

        measureDimensions();
    }, [style, minWidth, isLoading, label, statusText]);

    // Handle button click
    const handleClick = async (e: React.MouseEvent) => {
        if (disabled || isLoading) return;

        if (onClick) {
            await onClick(e);
        }

        // Call onComplete if provided (for external control of loading state)
        if (onComplete) {
            onComplete(e);
        }
    };

    // Combine refs (internal containerRef and forwarded ref)
    const combinedRef = (node: HTMLDivElement | null) => {
        containerRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }
    };

    // Render micro style button
    const renderMicro = () => {
        const isButtonDisabled = disabled || (isLoading && showLoadingState);
        const isLoadingState = isLoading && showLoadingState;

        return (
            <div
                ref={combinedRef}
                className="inline-flex items-center justify-center align-middle"
                style={{
                    width: containerDimensions?.width ? `${containerDimensions.width}px` : '95px',
                    minWidth: containerDimensions?.width ? `${containerDimensions.width}px` : '95px',
                    height: '100%',
                    opacity: useFadeAnimation ? (isVisible ? 1 : 0) : 1,
                    transition: useFadeAnimation
                        ? `opacity ${isVisible ? fadeInDuration : fadeOutDuration}ms cubic-bezier(0.4, 0.0, 0.2, 1)`
                        : 'none',
                }}
            >
                {isLoading && showLoadingState ? (
                    <div className="flex items-center justify-center gap-2 w-full h-full">
                        <div className={`animate-spin rounded-full h-4 w-4 border-2 ${colors.spinner} border-t-transparent flex-shrink-0`}></div>
                        <span className={`text-xs ${colors.text} font-medium whitespace-nowrap`}>{statusText}</span>
                    </div>
                ) : (
                    <div className="flex items-center justify-center w-full h-full">
                        <button
                            ref={buttonRef}
                            onClick={handleClick}
                            disabled={isButtonDisabled}
                            className={`p-0.5 ${isLoadingState ? colors.enabled : (disabled ? colors.disabled : colors.enabled)} ${isButtonDisabled ? '' : colors.hover} text-white rounded shadow border ${isLoadingState ? colors.border : (disabled ? 'border-gray-400' : colors.border)} flex items-center justify-center ${isButtonDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} transition-colors`}
                            title={title}
                        >
                            {icon}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // Render normal style button
    const renderNormal = () => {
        const buttonStyle: React.CSSProperties = minWidth ? { minWidth: `${minWidth}px` } : {};
        const isButtonDisabled = disabled || (isLoading && showLoadingState);
        const isLoadingState = isLoading && showLoadingState;

        return (
            <div
                ref={combinedRef}
                className="inline-flex"
                style={{
                    opacity: useFadeAnimation ? (isVisible ? 1 : 0) : 1,
                    transition: useFadeAnimation
                        ? `opacity ${isVisible ? fadeInDuration : fadeOutDuration}ms cubic-bezier(0.4, 0.0, 0.2, 1)`
                        : 'none',
                }}
            >
                <button
                    ref={buttonRef}
                    onClick={handleClick}
                    disabled={isButtonDisabled}
                    className={`h-8 px-3 py-2 ${isLoadingState ? colors.enabled : (disabled ? colors.disabled : colors.enabled)} ${isButtonDisabled ? '' : colors.hover} text-white rounded-lg shadow border ${isLoadingState ? colors.border : (disabled ? 'border-gray-400' : colors.border)} flex items-center justify-center gap-1.5 ${isButtonDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} transition-colors`}
                    title={title}
                    style={buttonStyle}
                >
                    {isLoading && showLoadingState ? (
                        <>
                            <div className={`animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent flex-shrink-0`}></div>
                            <span className="text-sm font-semibold whitespace-nowrap">{statusText}</span>
                        </>
                    ) : (
                        <>
                            {icon}
                            {label && <span className="text-sm font-semibold whitespace-nowrap">{label}</span>}
                        </>
                    )}
                </button>
            </div>
        );
    };

    return style === "micro" ? renderMicro() : renderNormal();
});

CindraButton.displayName = 'CindraButton';
